'use client';

import { useEffect, useRef } from 'react';
import { nb } from '@/lib/nobreak';
import type { HeroVisualBase } from './types';

/**
 * HERO VISUAL: TIMER (speed to lead). A stopwatch: the ring sweeps from the moment a borrower hits submit to the first words, the lead
 * chip arrives, three alert channels light in order, and the ring turns lime at the connect moment.
 * Every word comes from frontmatter props; the stopwatch scale (`limitSeconds`) and connect time (`connectSeconds`) are the page's own
 * "under 30 seconds" / "second 20" timeline. Server render = the FINAL frame (all lit, ring swept, readout at the connect time), so
 * crawlers, reduced motion and no-JS see the finished state; the loop only runs in the browser, only while on screen.
 * Motion is transform / opacity / stroke-dashoffset only (plus the readout text), one requestAnimationFrame loop, no libraries.
 */

type Channel = { icon?: 'phone' | 'text' | 'crm'; label: string; detail?: string; at?: number };
type Props = HeroVisualBase & {
  eyebrow?: string;
  limit?: string;
  limitSeconds?: number;
  connectSeconds?: number;
  readoutLabel?: string;
  connectedLabel?: string;
  lead?: { name?: string; detail?: string };
  channels?: Channel[];
};

const CX = 160, CY = 186, R = 122, CIRC = 2 * Math.PI * R;
const f2 = (n: number) => n.toFixed(2);
const clock = (s: number) => `0:${String(Math.max(0, Math.round(s))).padStart(2, '0')}`;

function Glyph({ name }: { name: 'phone' | 'text' | 'crm' }) {
  const p = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  if (name === 'phone') return <svg {...p}><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" /></svg>;
  if (name === 'text') return <svg {...p}><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1.1-4.3A8 8 0 1 1 21 12z" /><path d="M9 11h6M9 14h3.5" /></svg>;
  return <svg {...p}><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3 10h18M8 15h4" /></svg>;
}

const CSS = `
.hv-t{--hv-violet:#7c5cff;--hv-lime:#b6ff5c;position:relative;margin-inline:auto;width:100%;min-width:0;max-width:540px;padding-top:6px;color:var(--sk-ink,#fff)}
.hv-t *{text-shadow:none!important}
.hv-t-stage{position:relative;height:332px}
.hv-t-watch{position:absolute;left:50%;top:0;width:320px;height:332px;margin-left:-160px}
/* STATIC soft shadow under the watch. It used to be a drop-shadow FILTER on the drawing itself, which Chrome re-blurs on every frame while the ring sweeps (heavy on the GPU; the likely cause of owner-reported tab crashes). A separate, never-changing layer costs nothing per frame. */
.hv-t-watch::before{content:"";position:absolute;left:8%;right:8%;bottom:-6px;height:44px;border-radius:50%;background:radial-gradient(ellipse at 50% 50%,rgba(8,6,30,.6),rgba(8,6,30,0) 70%);transform:translateZ(0)}
html[data-skin-mode="light"] .hv-t-watch::before{background:radial-gradient(ellipse at 50% 50%,rgba(60,40,140,.42),rgba(60,40,140,0) 70%)}
.hv-t-watch svg{position:relative;display:block}
.hv-t-watch svg{display:block;width:100%;height:100%;overflow:visible}
.hv-t-arc{transition:none}
.hv-t[data-reset="1"] .hv-t-arc,.hv-t[data-reset="1"] .hv-t-hand{transition:stroke-dashoffset .7s cubic-bezier(.5,0,.2,1),transform .7s cubic-bezier(.5,0,.2,1)}
.hv-t-hand{transform-origin:${CX}px ${CY}px}
.hv-t-lime{opacity:0;transition:opacity .45s ease}
.hv-t[data-conn="1"] .hv-t-lime{opacity:1}
.hv-t-glow{opacity:0;transform-origin:${CX}px ${CY}px;transform-box:view-box}
.hv-t[data-conn="1"] .hv-t-glow{animation:hv-t-pulse 1.5s ease-out 1 both}
@keyframes hv-t-pulse{0%{opacity:.7;transform:scale(1)}100%{opacity:0;transform:scale(1.13)}}
.hv-t-center{position:absolute;left:50%;top:${CY}px;width:200px;margin-left:-100px;transform:translateY(-50%);text-align:center;color:#fff}
.hv-t-read{display:block;font-family:var(--font-display),sans-serif;font-weight:800;font-size:3.5rem;line-height:1;letter-spacing:-.035em;font-variant-numeric:tabular-nums;white-space:nowrap;transition:color .35s ease}
.hv-t[data-conn="1"] .hv-t-read{color:var(--hv-lime)}
.hv-t-sub{display:block;margin-top:8px;font-size:12px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,.72)}
.hv-t-phone{position:relative;display:grid;place-items:center;width:42px;height:42px;margin:0 auto 12px;border-radius:999px;background:rgba(255,255,255,.08);color:#fff;box-shadow:inset 0 0 0 1px rgba(255,255,255,.16);transition:background .35s ease,color .35s ease}
.hv-t[data-conn="1"] .hv-t-phone{background:var(--hv-lime);color:#05060b;box-shadow:none}
.hv-t-phone svg{width:20px;height:20px}
.hv-t-ring{position:absolute;inset:0;border-radius:999px;box-shadow:0 0 0 1.5px rgba(255,255,255,.35);opacity:0}
.hv-t[data-on="1"]:not([data-conn="1"]) .hv-t-ring{animation:hv-t-ring 1.4s ease-out infinite}
.hv-t[data-on="1"]:not([data-conn="1"]) .hv-t-ring+.hv-t-ring{animation-delay:.5s}
@keyframes hv-t-ring{0%{opacity:.8;transform:scale(1)}100%{opacity:0;transform:scale(1.9)}}
.hv-t[data-on="1"]:not([data-conn="1"]) .hv-t-phone svg{animation:hv-t-buzz 1.4s ease-in-out infinite}
@keyframes hv-t-buzz{0%,55%,100%{transform:rotate(0)}8%{transform:rotate(-14deg)}16%{transform:rotate(12deg)}24%{transform:rotate(-10deg)}32%{transform:rotate(8deg)}40%{transform:rotate(0)}}
.hv-t-tag{position:absolute;left:50%;bottom:50px;transform:translateX(-50%) translateY(4px);display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:999px;background:var(--hv-lime);color:#05060b;font-size:12px;font-weight:800;letter-spacing:.04em;opacity:0;transition:opacity .4s ease,transform .4s ease;white-space:nowrap}
.hv-t[data-conn="1"] .hv-t-tag{opacity:1;transform:translateX(-50%) translateY(0)}
.hv-t-lead{position:absolute;left:0;top:44px;z-index:2;display:flex;align-items:center;gap:10px;max-width:196px;padding:10px 14px 10px 10px;border-radius:18px;background:var(--sk-card,rgba(14,16,32,.9));color:var(--sk-ink,#fff);box-shadow:inset 0 0 0 1px var(--sk-line,rgba(255,255,255,.14)),var(--sk-drop,0 20px 40px -20px rgba(0,0,0,.8));transition:opacity .5s ease,transform .5s cubic-bezier(.2,.8,.2,1);backdrop-filter:blur(8px)}
.hv-t[data-chip="0"] .hv-t-lead{opacity:0;transform:translateX(-18px)}
.hv-t-av{flex:none;display:grid;place-items:center;width:34px;height:34px;border-radius:999px;background:linear-gradient(135deg,#7c5cff,#22e4ff);color:#05060b;font-family:var(--font-display),sans-serif;font-weight:800;font-size:15px}
.hv-t-lead b{display:block;font-size:14px;line-height:1.2}
.hv-t-lead i{display:block;font-style:normal;margin-top:2px;font-size:12px;line-height:1.3;color:var(--sk-mut,rgba(255,255,255,.64))}
.hv-t-limit{position:absolute;right:0;top:2px;z-index:2;max-width:150px;text-align:right}
.hv-t-limit b{display:block;white-space:nowrap;font-family:var(--font-display),sans-serif;font-size:1.05rem;line-height:1.15;letter-spacing:-.01em}
.hv-t-limit i{display:block;font-style:normal;margin-top:4px;font-size:12px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--sk-mut,rgba(255,255,255,.64))}
.hv-t-chs{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-top:18px}
.hv-t-ch{position:relative;display:flex;flex-direction:column;gap:8px;padding:12px 12px 13px;border-radius:18px;background:var(--sk-card,rgba(14,16,32,.9));box-shadow:inset 0 0 0 1px var(--sk-line,rgba(255,255,255,.14))}
.hv-t-ch::before{content:"";position:absolute;inset:0;border-radius:18px;background:rgba(124,92,255,.16);box-shadow:inset 0 0 0 1.5px var(--hv-violet);opacity:0;transition:opacity .4s ease}
.hv-t-ch[data-on="1"]::before{opacity:1}
.hv-t-ico{position:relative;display:grid;place-items:center;width:34px;height:34px;border-radius:999px;background:var(--sk-soft,rgba(255,255,255,.07));color:var(--sk-ink,#fff);overflow:hidden}
.hv-t-ico::after{content:"";position:absolute;inset:0;background:var(--hv-violet);opacity:0;transition:opacity .4s ease}
.hv-t-ch[data-on="1"] .hv-t-ico::after{opacity:1}
.hv-t-ico svg{position:relative;z-index:1;transition:color .4s ease}
.hv-t-ch[data-on="1"] .hv-t-ico{color:#fff}
.hv-t-ch b{position:relative;display:block;font-size:14px;line-height:1.2;color:var(--sk-ink,#fff)}
.hv-t-ch i{position:relative;display:block;font-style:normal;font-size:12px;line-height:1.35;color:var(--sk-mut,rgba(255,255,255,.64))}
.hv-t-ok{position:absolute;right:10px;top:10px;z-index:1;display:grid;place-items:center;width:18px;height:18px;border-radius:999px;background:var(--hv-lime);color:#05060b;opacity:0;transform:scale(.5);transition:opacity .3s ease,transform .35s cubic-bezier(.3,1.6,.5,1)}
.hv-t-ch[data-on="1"] .hv-t-ok{opacity:1;transform:scale(1)}
.hv-t-cap{margin:14px 0 0;text-align:center;font-size:12px;line-height:1.4;color:var(--sk-mut,rgba(255,255,255,.64))}
@media (prefers-reduced-motion:reduce){.hv-t *{animation:none!important;transition:none!important}}
`;

export function HeroTimer(props: Props) {
  const {
    ariaLabel = 'Illustration of a stopwatch timing the first call to a new lead',
    caption,
    eyebrow = 'Response timer',
    limit = 'Under 30 seconds',
    limitSeconds = 30,
    connectSeconds = 20,
    readoutLabel = 'To first words',
    connectedLabel = 'Connected',
    lead = {},
    channels = [],
  } = props;

  const rootRef = useRef<HTMLDivElement>(null);
  const arcA = useRef<SVGCircleElement>(null);
  const arcB = useRef<SVGCircleElement>(null);
  const hand = useRef<SVGGElement>(null);
  const read = useRef<HTMLSpanElement>(null);

  const frac = Math.min(1, connectSeconds / limitSeconds);
  const finalOffset = CIRC * (1 - frac);
  const finalDeg = frac * 360;
  const chs = channels.slice(0, 3);
  const initial = (lead.name ?? 'L').trim().charAt(0).toUpperCase();

  // Tick marks: one per second of the scale, a longer one every 5 s. Fixed rounding so server and client markup match.
  const ticks = Array.from({ length: limitSeconds }, (_, i) => {
    const a = (i / limitSeconds) * Math.PI * 2 - Math.PI / 2;
    const major = i % 5 === 0;
    const r1 = 134, r2 = major ? 143 : 139;
    return { x1: CX + Math.cos(a) * r1, y1: CY + Math.sin(a) * r1, x2: CX + Math.cos(a) * r2, y2: CY + Math.sin(a) * r2, major };
  });

  useEffect(() => {
    const root = rootRef.current;
    if (!root || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const SWEEP_AT = 1000, SWEEP = 4800, HOLD = 4300, RESET = 900;
    const CYCLE = SWEEP_AT + SWEEP + HOLD + RESET;
    const arcs = [arcA.current, arcB.current];
    const chNodes = Array.from(root.querySelectorAll<HTMLElement>('[data-ch]'));
    const times = chNodes.map((n) => Number(n.dataset.at ?? 0));
    let last = 0, clockMs = 0, raf = 0, visible = false, running = false, lastReadout = '', resetting = false;

    const set = (k: string, v: string) => { if (root.getAttribute(k) !== v) root.setAttribute(k, v); };
    const paint = (e: number, t: number) => {
      const f = Math.min(1, e / limitSeconds);
      arcs.forEach((a) => a && (a.style.strokeDashoffset = String(CIRC * (1 - f))));
      if (hand.current) hand.current.style.transform = `rotate(${f * 360}deg)`;
      const txt = clock(e);
      if (txt !== lastReadout && read.current) { read.current.textContent = txt; lastReadout = txt; }
      chNodes.forEach((n, i) => { const on = e >= times[i] && t >= SWEEP_AT ? '1' : '0'; if (n.dataset.on !== on) n.dataset.on = on; });
    };

    const frame = (now: number) => {
      raf = 0;
      if (!visible) { running = false; return; }
      const dt = Math.min(100, now - (last || now));
      last = now;
      clockMs = (clockMs + dt) % CYCLE;
      const t = clockMs;
      if (t >= CYCLE - RESET) {
        if (!resetting) {
          resetting = true;
          set('data-reset', '1'); set('data-conn', '0'); set('data-chip', '0'); set('data-on', '0');
          paint(0, 0); lastReadout = '';
        }
      } else {
        if (resetting) { resetting = false; set('data-reset', '0'); }
        const e = t < SWEEP_AT ? 0 : Math.min(connectSeconds, ((t - SWEEP_AT) / SWEEP) * connectSeconds);
        set('data-chip', t >= 450 ? '1' : '0');
        set('data-on', t >= SWEEP_AT && t < SWEEP_AT + SWEEP ? '1' : '0');
        set('data-conn', t >= SWEEP_AT + SWEEP ? '1' : '0');
        paint(e, t);
      }
      raf = requestAnimationFrame(frame);
    };
    const start = () => { if (!running) { running = true; last = 0; raf = requestAnimationFrame(frame); } };

    // Opening frame, then play only while on screen (and never on phones/tablets where the visual is display:none).
    set('data-chip', '0'); set('data-on', '0'); set('data-conn', '0'); set('data-reset', '0');
    paint(0, 0);
    const io = new IntersectionObserver((es) => { visible = (es[0]?.isIntersecting ?? false) && !document.hidden; if (visible) start(); }, { threshold: 0.2 });
    io.observe(root);
    const onVis = () => { if (document.hidden) { visible = false; } else if (root.getBoundingClientRect().bottom > 0) { visible = true; start(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => { visible = false; running = false; document.removeEventListener('visibilitychange', onVis); io.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [connectSeconds, limitSeconds]);

  return (
    <div ref={rootRef} className="hv-t hidden lg:block" role="img" aria-label={ariaLabel} data-chip="1" data-on="0" data-conn="1" data-reset="0">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div className="hv-t-stage">
        <div className="hv-t-watch">
          <svg viewBox="0 0 320 332" aria-hidden="true" focusable="false">
            <defs>
              <linearGradient id="hv-t-g" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#ff7cc6" /><stop offset=".5" stopColor="#7c5cff" /><stop offset="1" stopColor="#22e4ff" />
              </linearGradient>
              <radialGradient id="hv-t-body" cx=".5" cy=".38" r=".75">
                <stop offset="0" stopColor="#1d1f3a" /><stop offset="1" stopColor="#090a14" />
              </radialGradient>
              <linearGradient id="hv-t-bezel" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#7c5cff" stopOpacity=".85" /><stop offset=".5" stopColor="#ffffff" stopOpacity=".16" /><stop offset="1" stopColor="#22e4ff" stopOpacity=".5" />
              </linearGradient>
            </defs>
            <rect x="136" y="4" width="48" height="16" rx="6" fill="#1a1c33" stroke="url(#hv-t-bezel)" strokeWidth="1.5" />
            <rect x="150" y="18" width="20" height="26" rx="3" fill="#10122a" />
            <rect x="262" y="58" width="26" height="12" rx="5" fill="#1a1c33" stroke="url(#hv-t-bezel)" strokeWidth="1.5" transform="rotate(42 275 64)" />
            <circle cx={CX} cy={CY} r="145" fill="url(#hv-t-body)" stroke="url(#hv-t-bezel)" strokeWidth="2" />
            <circle cx={CX} cy={CY} r="106" fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="1" />
            {ticks.map((t, i) => <line key={i} x1={f2(t.x1)} y1={f2(t.y1)} x2={f2(t.x2)} y2={f2(t.y2)} stroke={t.major ? 'rgba(255,255,255,.7)' : 'rgba(255,255,255,.28)'} strokeWidth={t.major ? 2 : 1.2} strokeLinecap="round" />)}
            <circle cx={CX} cy={CY} r={R} fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="12" />
            <circle ref={arcA} className="hv-t-arc" cx={CX} cy={CY} r={R} fill="none" stroke="url(#hv-t-g)" strokeWidth="12" strokeLinecap="round" strokeDasharray={f2(CIRC)} strokeDashoffset={f2(finalOffset)} transform={`rotate(-90 ${CX} ${CY})`} />
            <circle ref={arcB} className="hv-t-arc hv-t-lime" cx={CX} cy={CY} r={R} fill="none" stroke="#b6ff5c" strokeWidth="12" strokeLinecap="round" strokeDasharray={f2(CIRC)} strokeDashoffset={f2(finalOffset)} transform={`rotate(-90 ${CX} ${CY})`} />
            <circle className="hv-t-glow" cx={CX} cy={CY} r={R + 6} fill="none" stroke="#b6ff5c" strokeWidth="3" />
            <g ref={hand} className="hv-t-hand" style={{ transform: `rotate(${f2(finalDeg)}deg)` }}>
              <circle cx={CX} cy={CY - R} r="9" fill="#fff" />
              <circle cx={CX} cy={CY - R} r="4" fill="#05060b" />
            </g>
          </svg>
          <div className="hv-t-center">
            <span className="hv-t-phone" aria-hidden="true"><span className="hv-t-ring" /><span className="hv-t-ring" /><Glyph name="phone" /></span>
            <span ref={read} className="hv-t-read">{nb(clock(connectSeconds))}</span>
            <span className="hv-t-sub">{readoutLabel}</span>
          </div>
          <span className="hv-t-tag"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>{connectedLabel}</span>
        </div>

        <div className="hv-t-lead">
          <span className="hv-t-av" aria-hidden="true">{initial}</span>
          <span><b>{lead.name ?? 'New lead'}</b><i>{nb(lead.detail ?? 'just submitted')}</i></span>
        </div>
        <div className="hv-t-limit"><b>{nb(limit)}</b><i>{eyebrow}</i></div>
      </div>

      {chs.length > 0 && (
        <div className="hv-t-chs">
          {chs.map((c, i) => (
            <div key={c.label} className="hv-t-ch" data-ch data-at={c.at ?? 11 + i * 2} data-on="1">
              <span className="hv-t-ok" aria-hidden="true"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg></span>
              <span className="hv-t-ico"><Glyph name={c.icon ?? (['phone', 'text', 'crm'] as const)[i]} /></span>
              <span><b>{nb(c.label)}</b>{c.detail && <i>{nb(c.detail)}</i>}</span>
            </div>
          ))}
        </div>
      )}
      {caption && <p className="hv-t-cap">{nb(caption)}</p>}
    </div>
  );
}
